import {
  Link,
  Text,
  Wrap,
} from '@chakra-ui/react';
import {
  useMaintenanceOperations,
} from '@src/modules/performance/context/reports-api-hooks';
import { selectedDatabaseIdState, currentDatabaseSelector } from '@transport-insights/uikit';
import { useRecoilValue } from 'recoil';
import LineRangeAreaChart from '@src/modules/performance/components/charts/LineRangeAreaChart';
import { CHART_COLOURS } from '@src/modules/performance/shared/constants';
import { filterChartDataByName } from '@src/modules/performance/shared/helpers';
import ReportSection from './ReportSection';

const informationSource = {
  title: 'Cost Efficiency - Maintenance, Operations and Renewals',
  content: [
    {
      title: 'Maintenance, operations, and renewals expenditure / length ($1,000 / km)',
      description:
  <Text fontSize="inherit">
    This result has been calculated by Road Efficiency Group Te Ringa Maimoa using the co-funded expenditure on maintenance, operations, and renewals (excluding Emergency Works) per kilometre of the road network (below). Reported in thousand dollars per kilometre.
  </Text>,
      source: null,
    },
    {
      title: 'Maintenance, operations, and renewals expenditure / lane km ($1,000 / km)',
      description:
  <Text fontSize="inherit">
    This result has been calculated by Road Efficiency Group Te Ringa Maimoa using the co-funded expenditure on maintenance, operations, and renewals (excluding Emergency Works) divided by the total Lane kilometres of the road network. Reported in thousand dollars per kilometre.
  </Text>,
      source: null,
    },
    {
      title: 'Maintenance, operations, and renewals expenditure / VKT ($1,000 / MVKT)',
      description:
  <Text fontSize="inherit">
    This result has been calculated by Road Efficiency Group Te Ringa Maimoa using the co-funded expenditure on maintenance, operations, and renewals (excluding Emergency Works) divided by the total volume of vehicle travel on the road network. Reported in thousand dollars per million vehicle kilometres travelled (MVKT).
  </Text>,
      source:
  <Text fontSize="inherit" as="span">
    Sourced from
    {' '}
    <Link href="https://www.nzta.govt.nz/planning-and-investment/learning-and-resources/transport-data/data-and-tools/" isExternal>NZTA Data and Tools</Link>
  </Text>,
    },
  ],
};

function TransportProgrammeDeliveryCostEfficiencyMaintenance() {
  const rcaId = useRecoilValue(selectedDatabaseIdState);
  const currentDatabase = useRecoilValue(currentDatabaseSelector);
  const { data: maintenanceOperationsData, isLoading: isLoadingMaintenance } = useMaintenanceOperations();
  const isLoading = isLoadingMaintenance;
  const isRegionNationalOrStateHighways = rcaId >= 1000 || rcaId === 81;
  const isNational = rcaId === 1000;
  let chartFilters = [];
  if (isNational) {
    chartFilters = ['Peer', 'Percentile'];
  } else if (isRegionNationalOrStateHighways) {
    chartFilters = ['Peer'];
  }

  const maintenanceOperationsYears = [...new Set(maintenanceOperationsData?.map((item) => item.Year))];

  // Maintenance Operations Length Chart
  const maintenanceOperationsLengthChartData = [{
    name: currentDatabase?.name,
    type: 'line',
    data: maintenanceOperationsData?.map((item) => ({ x: item.Year, y: item.Ratio1000km })),
    color: CHART_COLOURS.blue,
  }, {
    name: 'Peer Group',
    type: 'line',
    data: maintenanceOperationsData?.map((item) => ({ x: item.Year, y: item.Ratio1000kmPG })),
    color: CHART_COLOURS.black,
  }, {
    name: '25-75th Percentile',
    type: 'rangeArea',
    data: maintenanceOperationsData?.map((item) => ({ x: item.Year, y: [item.LowPercentile, item.HighPercentile] })),
    color: CHART_COLOURS.lightgrey,
  }];

  // Filter out the Peer Group and Percentile data if it's a national report, and Peer Group if it's a region or state highways report
  const maintenanceOperationsLengthChartSeries = filterChartDataByName(
    maintenanceOperationsLengthChartData,
    chartFilters,
  );

  // Maintenance Operations Lane KM Chart
  const maintenanceOperationsLaneKmChartData = [{
    name: currentDatabase?.name,
    type: 'line',
    data: maintenanceOperationsData?.map((item) => ({ x: item.Year, y: item.Ratio1000LaneKm })),
    color: CHART_COLOURS.blue,
  }, {
    name: 'Peer Group',
    type: 'line',
    data: maintenanceOperationsData?.map((item) => ({ x: item.Year, y: item.Ratio1000LaneKmPG })),
    color: CHART_COLOURS.black,
  }, {
    name: '25-75th Percentile',
    type: 'rangeArea',
    data: maintenanceOperationsData?.map((item) => ({ x: item.Year, y: [item.LowPercentileLaneKm, item.HighPercentileLaneKm] })),
    color: CHART_COLOURS.lightgrey,
  }];

  // Filter out the Peer Group and Percentile data if it's a national report, and Peer Group if it's a region or state highways report
  const maintenanceOperationsLaneKmChartSeries = filterChartDataByName(
    maintenanceOperationsLaneKmChartData,
    chartFilters,
  );

  // Maintenance Operations VKT Chart
  const maintenanceOperationsVktChartData = [{
    name: currentDatabase?.name,
    type: 'line',
    data: maintenanceOperationsData?.map((item) => ({ x: item.Year, y: item.RatioMVKT })),
    color: CHART_COLOURS.blue,
  }, {
    name: 'Peer Group',
    type: 'line',
    data: maintenanceOperationsData?.map((item) => ({ x: item.Year, y: item.RatioMVKTPG })),
    color: CHART_COLOURS.black,
  }, {
    name: '25-75th Percentile',
    type: 'rangeArea',
    data: maintenanceOperationsData?.map((item) => ({ x: item.Year, y: [item.LowPercentileMVKT, item.HighPercentileMVKT] })),
    color: CHART_COLOURS.lightgrey,
  }];
  // Filter out the Peer Group and Percentile data if it's a national report, and Peer Group if it's a region or state highways report
  const maintenanceOperationsVktChartSeries = filterChartDataByName(
    maintenanceOperationsVktChartData,
    chartFilters,
  );

  const markers = isRegionNationalOrStateHighways ? {
    size: [3, 0],
    hover: {
      size: 6,
    },
  } : {
    size: [3, 3, 0],
    hover: {
      size: 6,
    },
  };

  const stroke = isRegionNationalOrStateHighways ? {
    curve: 'straight',
    width: [2, 0],
  } : {
    curve: 'straight',
    width: [2, 2, 0],
  };

  return (
    <ReportSection
      title="Cost Efficiency - Maintenance, Operations and Renewals"
      informationSource={informationSource}
      links={[
        {
          label: 'ONF Maintenance Costs',
          url: '/onf/transport-outcomes/ep3',
          isExternal: false,
          userRoles: null,
        },
      ]}
      isLoading={isLoading || !maintenanceOperationsData}
      lockYear={maintenanceOperationsData?.[0]?.LockYear}
    >
      <Wrap spacing={0}>
        <LineRangeAreaChart
          title={['Maintenance, operations and renewals', 'expenditure/length ($1,000/km)']}
          series={maintenanceOperationsLengthChartSeries}
          type="rangeArea"
          width="280"
          height="280"
          categories={maintenanceOperationsYears}
          markers={markers}
          stroke={stroke}
          prefix="$"
          suffix=""
          decimalPoints={1}
          yAxisDecimalPoints={0}
        />
        <LineRangeAreaChart
          title={['Maintenance, operations and renewals', 'expenditure/lane km ($1,000/km)']}
          series={maintenanceOperationsLaneKmChartSeries}
          type="rangeArea"
          width="280"
          height="280"
          categories={maintenanceOperationsYears}
          markers={markers}
          stroke={stroke}
          prefix="$"
          suffix=""
          decimalPoints={1}
          yAxisDecimalPoints={0}
        />
        <LineRangeAreaChart
          title={['Maintenance, operations and renewals', 'expenditure/VKT ($1,000/MVKT)']}
          series={maintenanceOperationsVktChartSeries}
          type="rangeArea"
          width="280"
          height="280"
          categories={maintenanceOperationsYears}
          markers={markers}
          stroke={stroke}
          prefix="$"
          suffix=""
          decimalPoints={1}
          yAxisDecimalPoints={0}
        />
      </Wrap>
    </ReportSection>
  );
}

export default TransportProgrammeDeliveryCostEfficiencyMaintenance;
